import { useEffect, useState } from 'react';
import type { Profile, Recommendation } from '../api/types';
import { SOURCE_LABELS } from '../api/types';
import hubScene from './hubScene';
import type { StationStop } from './lines';
import { Scenery } from './Scenery';
import { prefetchScene } from './scenes';
import { Wordmark } from './Wordmark';

interface Props {
  network: StationStop[];
  profile: Profile;
  total: number;
  onRide: (stop: StationStop) => void;
  onLeave: () => void;
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

function useClock() {
  const read = () => {
    const d = new Date();
    return {
      time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      date: `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(
        d.getDate(),
      ).padStart(2, '0')} ${WEEKDAYS[d.getDay()]}曜`,
    };
  };
  const [now, setNow] = useState(read);
  useEffect(() => {
    const t = window.setInterval(() => setNow(read()), 15000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

/** "TV ・ 2014 ・ A-1 Pictures" — whatever AniList has, in that order. */
function facts(rec: Recommendation): string {
  return [rec.format?.replace(/_/g, ' '), rec.season_year, rec.studio]
    .filter((f) => f !== null && f !== undefined && f !== '')
    .join(' ・ ');
}

/**
 * Said out loud when the list on the board is not quite the one the rider asked
 * for. The same-name account in particular might be somebody else entirely.
 */
export function serviceNotice(profile: Profile): string | null {
  if (profile.fallback_from) {
    const from = SOURCE_LABELS[profile.fallback_from].toUpperCase();
    const to = SOURCE_LABELS[profile.source].toUpperCase();
    return `振替輸送 ・ ${from} ISN'T ANSWERING, SO THIS IS THE ${to} ACCOUNT WITH THE SAME NAME`;
  }
  if (profile.stale_seconds != null) {
    const from = SOURCE_LABELS[profile.source].toUpperCase();
    const minutes = Math.max(1, Math.round(profile.stale_seconds / 60));
    const age = minutes < 90 ? `${minutes} MIN` : `${Math.round(minutes / 60)} HR`;
    return `遅延 ・ ${from} IS BUSY, SO THIS IS YOUR LIST FROM ${age} AGO`;
  }
  return null;
}

export function Hub({ network, profile, total, onRide, onLeave }: Props) {
  const clock = useClock();
  const first = network[0];

  // The rider is about to pick a line; have the first one's backdrop ready.
  useEffect(() => {
    if (first) prefetchScene(first.line.slug);
  }, [first]);

  const unseen = profile.list_size - profile.matched_items;
  const thin = profile.matched_items > 0 && unseen / profile.list_size > 0.4;
  const notice = serviceNotice(profile);

  return (
    <>
      <Scenery scene={hubScene} />

      <div className="wrap">
        <header className="masthead">
          <div>
            <div className="mark">
              次 <Wordmark />
            </div>
            <div className="sub">
              <span>中央駅 ・ CENTRAL STATION</span>
              <span className="sep"> ・ </span>
              <span>
                {network.length} {network.length === 1 ? 'LINE' : 'LINES'} ・ {total}{' '}
                {total === 1 ? 'TITLE' : 'TITLES'}
              </span>
            </div>
          </div>
          <div className="clockbox">
            <div className="t">{clock.time}</div>
            <div className="d">{clock.date}</div>
          </div>
        </header>

        <section className="board">
          <div className="board-top">
            <span className="ttl">発車案内 DEPARTURES</span>
            <div className="marquee">
              <span>
                {profile.username.toUpperCase()} ・ {SOURCE_LABELS[profile.source].toUpperCase()} ・
                MATCHED AGAINST {profile.matched_items} OF {profile.list_size} ENTRIES ON YOUR LIST
                ・ CHOOSE A LINE OR RIDE THE WHOLE NETWORK ・ EACH PLATFORM PRINTS ONE TICKET AT A
                TIME ・
                {thin
                  ? ` ${unseen} ENTRIES ARE OUTSIDE THE CATALOGUE AND COUNT FOR NOTHING ・`
                  : ''}
              </span>
            </div>
          </div>

          <div className="cols" aria-hidden="true">
            <span>番線</span>
            <span>路線 LINE</span>
            <span>行先 FIRST TITLE</span>
            <span>種別 GENRE</span>
            <span className="r">本数</span>
            <span />
          </div>

          <div className="rows">
            {network.map((stop, i) => {
              const lead = stop.picks[0];
              if (!lead) return null;
              const leadTitle = lead.title_english ?? lead.title_romaji;
              return (
                <button
                  key={stop.line.slug}
                  className="row"
                  style={{ ['--c' as string]: stop.line.color, ['--i' as string]: i }}
                  onClick={() => onRide(stop)}
                  // The map below offers the same nine destinations, so each
                  // control has to say which one it is by more than the line name.
                  aria-label={`Platform ${stop.line.id}, ${stop.line.kanji} ${stop.line.lineEn}, ${
                    stop.picks.length
                  } ${stop.picks.length === 1 ? 'title' : 'titles'}, first ${leadTitle}`}
                >
                  <span className="plat">{stop.line.id}</span>
                  <span className="nm">
                    <span className="kj">{stop.line.kanji}</span>
                    <span className="en">{stop.line.lineEn}</span>
                  </span>
                  {/*
                    Where a real board prints the train's destination, this one
                    prints the first title the line is running — the reason to
                    board it — with the next few covers fanned behind it.
                  */}
                  <span className="dest">
                    <span className="covers" aria-hidden="true">
                      {stop.picks.slice(0, 3).map((p) =>
                        p.cover_image_url ? (
                          <img key={p.media_id} src={p.cover_image_url} alt="" loading="lazy" />
                        ) : (
                          <i key={p.media_id} />
                        ),
                      )}
                    </span>
                    <span className="dt">
                      <em>{facts(lead)}</em>
                      <b>{leadTitle}</b>
                    </span>
                  </span>
                  {/* Two columns on a wide board, one line of small print on a phone. */}
                  <span className="meta">
                    <span className="gn">
                      {stop.line.genreJp} {stop.line.genreEn}
                    </span>
                    <span className="ct">
                      <b>{stop.picks.length}</b>
                      {stop.picks.length === 1 ? 'TITLE' : 'TITLES'}
                    </span>
                  </span>
                  <span className="go">▶</span>
                </button>
              );
            })}
          </div>

          {/*
            The strip along the foot of a real board that says whether the
            lines are running. Here it is the one honest status there is: how
            much of the rider's list the model could read.
          */}
          <div className="board-foot">
            {notice ? (
              <span className="warn" role="status">
                {notice}
              </span>
            ) : (
              <span className="ok">
                平常運転 ・ GOOD SERVICE ON {network.length === 1 ? 'THE LINE' : `ALL ${network.length} LINES`}
              </span>
            )}
            <span className={thin ? 'warn' : undefined}>
              {profile.matched_items} / {profile.list_size} ENTRIES READ
            </span>
          </div>
        </section>

        <section className="map">
          <h2>路線図 ・ NETWORK MAP</h2>
          <div className="mapline">
            {network.map((stop) => (
              <button
                key={stop.line.slug}
                className="node"
                style={{ ['--c' as string]: stop.line.color }}
                onClick={() => onRide(stop)}
                aria-label={`${stop.line.kanji} ${stop.line.lineEn} on the network map`}
              >
                <i />
                <span className="kj">{stop.line.kanji}</span>
                <span className="en">{stop.line.lineEn}</span>
              </button>
            ))}
          </div>
        </section>

        <div className="cta">
          {first && (
            <button className="depart" onClick={() => onRide(first)}>
              <span className="l">出発 ・ RIDE THE LINE</span>
              <span className="r">
                START AT PLATFORM {first.line.id}
                <br />
                {first.line.kanji} {first.line.lineEn} ▶
              </span>
            </button>
          )}
          <p className="note">
            At each platform: <kbd>‹</kbd> <kbd>›</kbd> walks the titles ・ <kbd>shift</kbd> +{' '}
            <kbd>›</kbd> rides to the next station ・ swipe the ticket on touch ・ NEXT on the last
            ticket rides on.
          </p>
        </div>

        <p className="foot">
          <span>
            <Wordmark /> ・ 次の一本を選ぶ
          </span>
          <button className="linkish" onClick={onLeave}>
            別の利用者 ・ ANOTHER RIDER
          </button>
        </p>
      </div>
    </>
  );
}
